import React from 'react';
import ApexChartFields from './ApexChart/fields';
import CSVImportFields from './CSVImport/fields';
import QRCodeFields from './QRCode/fields';
import ChartSectionFields from './ChartSection/fields';
import FileUploaderFields from './FileUploader/fields';
import MapFields from './Map/fields';
import DateTimePickerFields from './DateTimePicker/fields';
import RelationalDiagramFields from './RelationalDiagram/fields';
import BigCalendarFields from './BigCalendar/fields';
import CalendarDatePickerFields from './CalendarDatePicker/fields';
import OAuthLoginResponseHandlerFields from './OAuthLoginResponseHandler/fields';
import CSVViewerFields from './CSVViewer/fields';
import ScoreIndicatorFields from './ScoreIndicator/fields';
import DoughnutChartFields from './DoughnutChart/fields';
import BarChartFields from './BarChart/fields';
import BatteryFields from './BatteryIndicator/fields';
import RadarChartFields from './RadarChart/fields';

const ApexChart = React.lazy(() => import('./ApexChart'));
const ScoreIndicator = React.lazy(() => import('./ScoreIndicator'));
const DoughnutChart = React.lazy(() => import('./DoughnutChart'));
const BarChart = React.lazy(() => import('./BarChart'));
const CSVImport = React.lazy(() => import('./CSVImport'));
const QRCode = React.lazy(() => import('./QRCode'));
const ChartSection = React.lazy(() => import('./ChartSection'));
const FileUploader = React.lazy(() => import('./FileUploader'));
const Map = React.lazy(() => import('./Map'));
const DateTimePicker = React.lazy(() => import('./DateTimePicker'));
const RelationalDiagram = React.lazy(() => import('./RelationalDiagram'));
const CSVViewer = React.lazy(() => import('./CSVViewer'));
const BigCalendar = React.lazy(() => import('./BigCalendar'));
const CalendarDatePicker = React.lazy(() => import('./CalendarDatePicker'));
const BatteryIndicator = React.lazy(() => import('./BatteryIndicator'));
const OAuthLoginResponseHandler = React.lazy(() =>
  import('./OAuthLoginResponseHandler')
);
const RadarChart = React.lazy(() => import('./RadarChart'));

const COMPONENT_NAMES = {
  APEX_CHART: 'ApexChart',
  BAR_CHART: 'BarChart',
  RADAR_CHART: 'RadarChart',
  BATTERY_INDICATOR: 'batteryindicator',
  DOUGHNUT_CHART: 'doughnutchart',
  SCORE_INDICATOR: 'ScoreIndicator',
  MAP: 'Map',
  QR_CODE: 'QRCode',
  CSV_IMPORT: 'CSVImport',
  CSV_VIEWER: 'CSVViewer',
  SMS_PHONE_VERIFICATION: 'SMSPhoneVerification',
  CHART_SECTION: 'ChartSection',
  FILE_UPLOADER: 'FileUploader',
  DATETIME_PICKER: 'DateTimePicker',
  RELATIONAL_DIAGRAM: 'RelationalDiagram',
  BIG_CALENDAR: 'BigCalendar',
  CALENDAR_DATE_PICKER: 'CalendarDatePicker',
  OAUTH_LOGIN_RESPONSE_HANDLE: 'OAuthLoginResponseHandler'
};

const lookupComponent = ({
  domNode,
  designMode,
  page,
  data,
  props = {},
  metadata = {},
  children,
  renderComponentMarkup = false
}) => {
  let component;
  let reactComponentName;

  switch (domNode.name?.toLowerCase()) {
    case COMPONENT_NAMES.OAUTH_LOGIN_RESPONSE_HANDLE.toLowerCase():
      component = (
        <OAuthLoginResponseHandler
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = FileUploaderFields.name;
      break;
    case COMPONENT_NAMES.FILE_UPLOADER.toLowerCase():
      component = (
        <FileUploader
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = FileUploaderFields.name;
      break;
    case COMPONENT_NAMES.APEX_CHART.toLowerCase():
      component = (
        <ApexChart
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = ApexChartFields.name;
      break;
    case COMPONENT_NAMES.SCORE_INDICATOR.toLowerCase():
      component = (
        <ScoreIndicator
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = ScoreIndicatorFields.name;
      break;
    case COMPONENT_NAMES.BATTERY_INDICATOR.toLowerCase():
      component = (
        <BatteryIndicator
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = BatteryFields.name;
      break;
    case COMPONENT_NAMES.DOUGHNUT_CHART.toLowerCase():
      component = (
        <DoughnutChart
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = DoughnutChartFields.name;
      break;
    case COMPONENT_NAMES.BAR_CHART.toLowerCase():
      component = (
        <BarChart
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = BarChartFields.name;
      break;
    case COMPONENT_NAMES.RADAR_CHART.toLowerCase():
      component = (
        <RadarChart
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = RadarChartFields.name;
      break;
    case COMPONENT_NAMES.CSV_VIEWER.toLowerCase():
      component = (
        <CSVViewer
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = CSVViewerFields.name;
      break;
    case COMPONENT_NAMES.BIG_CALENDAR.toLowerCase():
      component = (
        <BigCalendar
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = BigCalendarFields.name;
      break;
    case COMPONENT_NAMES.CALENDAR_DATE_PICKER.toLowerCase():
      component = (
        <CalendarDatePicker
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = CalendarDatePickerFields.name;
      break;
    case COMPONENT_NAMES.RELATIONAL_DIAGRAM.toLowerCase():
      component = (
        <RelationalDiagram
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = RelationalDiagramFields.name;
      break;
    case COMPONENT_NAMES.DATETIME_PICKER.toLowerCase():
      component = (
        <DateTimePicker
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = DateTimePickerFields.name;
      break;
    case COMPONENT_NAMES.MAP.toLowerCase():
      component = (
        <Map
          attributes={domNode.attribs}
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = ApexChartFields.name;
      break;
    case COMPONENT_NAMES.CHART_SECTION.toLowerCase():
      component = (
        <ChartSection
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = ChartSectionFields.name;
      break;
    case COMPONENT_NAMES.QR_CODE.toLowerCase():
      component = (
        <QRCode
          designMode={designMode}
          {...props}
          {...metadata}
          data={data}
          page={page}
          className="react-component"
          children={children}
        />
      );
      reactComponentName = QRCodeFields.name;
      break;
    case COMPONENT_NAMES.CSV_IMPORT.toLowerCase():
      component = (
        <CSVImport
          designMode={designMode}
          data={data}
          {...props}
          {...metadata}
          page={page}
          className="react-component"
        />
      );
      reactComponentName = CSVImportFields.name;
      break;
    default:
      return false;
  }

  if (renderComponentMarkup) {
    let markup = `<${reactComponentName} ${Object.keys(props).map(
      p => `${p}="${props[p]}"`
    )}`;
    if (domNode.children?.length > 0) {
      markup += '/>';
    } else {
      markup += '>';
    }
    return markup;
  }

  return (
    <div
      className="react-component"
      componentname={reactComponentName}
      id={props?.id}>
      {component}
    </div>
  );
};

const getComponentFields = name => {
  switch (name?.toLowerCase()) {
    case COMPONENT_NAMES.OAUTH_LOGIN_RESPONSE_HANDLE.toLowerCase():
      return OAuthLoginResponseHandlerFields;
    case COMPONENT_NAMES.CALENDAR_DATE_PICKER.toLowerCase():
      return CalendarDatePickerFields;
    case COMPONENT_NAMES.BIG_CALENDAR.toLowerCase():
      return BigCalendarFields;
    case COMPONENT_NAMES.APEX_CHART.toLowerCase():
      return ApexChartFields;
    case COMPONENT_NAMES.SCORE_INDICATOR.toLowerCase():
      return ScoreIndicatorFields;
    case COMPONENT_NAMES.BATTERY_INDICATOR.toLowerCase():
      return BatteryFields;
    case COMPONENT_NAMES.DOUGHNUT_CHART.toLowerCase():
      return DoughnutChartFields;
    case COMPONENT_NAMES.BAR_CHART.toLowerCase():
      return BarChartFields;
    case COMPONENT_NAMES.RADAR_CHART.toLowerCase():
      return RadarChartFields;
    case COMPONENT_NAMES.CSV_VIEWER.toLowerCase():
      return CSVViewerFields;
    case COMPONENT_NAMES.DATETIME_PICKER.toLowerCase():
      return DateTimePickerFields;
    case COMPONENT_NAMES.CSV_IMPORT.toLowerCase():
      return CSVImportFields;
    case COMPONENT_NAMES.QR_CODE.toLowerCase():
      return QRCodeFields;
    case COMPONENT_NAMES.CHART_SECTION.toLowerCase():
      return ChartSectionFields;
    case COMPONENT_NAMES.FILE_UPLOADER.toLowerCase():
      return FileUploaderFields;
    case COMPONENT_NAMES.MAP.toLowerCase():
      return MapFields;
    case COMPONENT_NAMES.RELATIONAL_DIAGRAM.toLowerCase():
      return RelationalDiagramFields;
    default:
      return { properties: [] };
  }
};

export { lookupComponent, getComponentFields };
